import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Button,
  Box,
  Card,
  CardContent,
  CardActions,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const Projects = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    current_address: '',
    rental_address: '',
    rebuilt_address: '',
    redevelopment_start_date: '',
    redevelopment_end_date: '',
    rental_start_date: '',
    rental_end_date: '',
    living_room: 1,
    bedrooms: 2,
    kitchen: 1,
    bathrooms: 2,
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API_URL}/projects`);
      setProjects(response.data.projects || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API_URL}/projects/${editingId}`, formData);
      } else {
        const response = await axios.post(`${API_URL}/projects`, formData);
        if (response.data.project) {
          // Show success message if baseline furniture was created
          const { living_room, bedrooms, kitchen } = formData;
          if ((living_room && parseInt(living_room) > 0) || 
              (bedrooms && parseInt(bedrooms) > 0) || 
              (kitchen && parseInt(kitchen) > 0)) {
            // Optional: Show notification that baseline furniture was created
            console.log('Project created with baseline furniture');
          }
        }
      }
      handleClose();
      fetchProjects();
    } catch (error) {
      console.error('Error saving project:', error);
      if (error.response) {
        alert(`Error: ${error.response.data.message || 'Failed to save project'}`);
      } else {
        alert('Failed to save project. Please try again.');
      }
    }
  };

  const handleClose = () => {
    setOpen(false);
    setEditingId(null);
      setFormData({
        name: '',
        current_address: '',
        rental_address: '',
        rebuilt_address: '',
        redevelopment_start_date: '',
        redevelopment_end_date: '',
        rental_start_date: '',
        rental_end_date: '',
        living_room: 1,
        bedrooms: 2,
        kitchen: 1,
        bathrooms: 2,
      });
  };

  const handleEdit = async (id) => {
    try {
      const response = await axios.get(`${API_URL}/projects/${id}`);
      const project = response.data.project;
      setFormData({
        name: project.name || '',
        current_address: project.current_address || '',
        rental_address: project.rental_address || '',
        rebuilt_address: project.rebuilt_address || '',
        redevelopment_start_date: project.redevelopment_start_date ? project.redevelopment_start_date.split('T')[0] : '',
        redevelopment_end_date: project.redevelopment_end_date ? project.redevelopment_end_date.split('T')[0] : '',
        rental_start_date: project.rental_start_date ? project.rental_start_date.split('T')[0] : '',
        rental_end_date: project.rental_end_date ? project.rental_end_date.split('T')[0] : '',
        living_room: project.living_room || 1,
        bedrooms: project.bedrooms || 2,
        kitchen: project.kitchen || 1,
        bathrooms: project.bathrooms || 2,
      });
      setEditingId(id);
      setOpen(true);
    } catch (error) {
      console.error('Error fetching project:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this project? This will also delete all associated furniture items.')) {
      try {
        await axios.delete(`${API_URL}/projects/${id}`);
        fetchProjects();
      } catch (error) {
        console.error('Error deleting project:', error);
        alert('Failed to delete project');
      }
    }
  };

  return (
    <Container sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          My Projects
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpen(true)}
        >
          New Project
        </Button>
      </Box>

      {projects.length === 0 ? (
        <Alert severity="info">No projects yet. Create your first project to get started!</Alert>
      ) : (
        <Grid container spacing={3}>
          {projects.map((project) => (
            <Grid item xs={12} sm={6} md={4} key={project.id}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {project.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Status: {project.status}
                  </Typography>
                  {project.current_address && (
                    <Typography variant="body2" color="text.secondary">
                      {project.current_address}
                    </Typography>
                  )}
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    onClick={() => navigate(`/projects/${project.id}`)}
                  >
                    View Details
                  </Button>
                  <Button size="small" startIcon={<EditIcon />} onClick={() => handleEdit(project.id)}>
                    Edit
                  </Button>
                  <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => handleDelete(project.id)}>
                    Delete
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{editingId ? 'Edit Project' : 'Create New Project'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              fullWidth
              label="Project Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              margin="normal"
            />
            <TextField
              fullWidth
              label="Current Address"
              name="current_address"
              value={formData.current_address}
              onChange={handleChange}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Rental Address"
              name="rental_address"
              value={formData.rental_address}
              onChange={handleChange}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Rebuilt Address"
              name="rebuilt_address"
              value={formData.rebuilt_address}
              onChange={handleChange}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Redevelopment Start Date"
              name="redevelopment_start_date"
              type="date"
              value={formData.redevelopment_start_date}
              onChange={handleChange}
              margin="normal"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              label="Redevelopment End Date"
              name="redevelopment_end_date"
              type="date"
              value={formData.redevelopment_end_date}
              onChange={handleChange}
              margin="normal"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              label="Rental Start Date"
              name="rental_start_date"
              type="date"
              value={formData.rental_start_date}
              onChange={handleChange}
              margin="normal"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              label="Rental End Date"
              name="rental_end_date"
              type="date"
              value={formData.rental_end_date}
              onChange={handleChange}
              margin="normal"
              InputLabelProps={{ shrink: true }}
            />
            <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
              Room Specifications
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Specify the number of rooms to automatically generate baseline furniture items
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Living Room"
                  name="living_room"
                  type="number"
                  value={formData.living_room}
                  onChange={handleChange}
                  margin="normal"
                  inputProps={{ min: 0, max: 5 }}
                  helperText="Number of living rooms"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Bedrooms"
                  name="bedrooms"
                  type="number"
                  value={formData.bedrooms}
                  onChange={handleChange}
                  margin="normal"
                  inputProps={{ min: 0, max: 10 }}
                  helperText="Number of bedrooms"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Kitchen"
                  name="kitchen"
                  type="number"
                  value={formData.kitchen}
                  onChange={handleChange}
                  margin="normal"
                  inputProps={{ min: 0, max: 3 }}
                  helperText="Number of kitchens"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Bathrooms"
                  name="bathrooms"
                  type="number"
                  value={formData.bathrooms}
                  onChange={handleChange}
                  margin="normal"
                  inputProps={{ min: 0, max: 10 }}
                  helperText="Number of bathrooms"
                />
              </Grid>
            </Grid>
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Baseline Furniture:</strong> Based on your room specifications, we'll automatically create typical furniture items with default dimensions and pricing. You can edit or delete these items later to match your specific needs.
              </Typography>
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingId ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Container>
  );
};

export default Projects;

